import React, { useState } from 'react';
import moment from 'moment';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import TodayIcon from '@mui/icons-material/Today';
import { Menu } from '@mui/material';
import './Calendar.scss';

export default function Calendar({
    open = false,
    onClose,
    anchorEl,
    highlightedDates = [],
    disabled = false,
}) {
    const className = 'calendar';

    const [currentDate, setCurrentDate] = useState(new Date());

    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const weekDays = [
        'Sun',
        'Mon',
        'Tue',
        'Wed',
        'Thu',
        'Fri',
        'Sat',
    ];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getMonthDetails = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        return {
            totalDays: lastDay.getDate(),
            startingDay: firstDay.getDay(),
        };
    };

    const moveMonth = (direction) => {
        if (disabled) return;

        setCurrentDate((prevDate) => {
            const newDate = new Date(prevDate);
            newDate.setMonth(newDate.getMonth() + direction);
            return newDate;
        });
    };

    const goToToday = () => {
        if (disabled) return;
        setCurrentDate(new Date());
    };

    const isSameDate = (date1, date2) => {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    };

    const renderCalendarDays = () => {
        const { totalDays, startingDay } = getMonthDetails();

        const days = [];

        // Empty cells
        for (let i = 0; i < startingDay; i++) {
            days.push(
                <div
                    key={`empty-${i}`}
                    className={`${className}__day ${className}__day--empty`}
                />
            );
        }

        // Calendar dates
        for (let day = 1; day <= totalDays; day++) {
            const date = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                day
            );

            date.setHours(0, 0, 0, 0);

            const dateStr = moment(date).format('DD MMM YYYY');

            const isHighlighted = highlightedDates.includes(dateStr);
            const isToday = isSameDate(date, today);
            const isPast = date < today;
            const isFuture = date > today;

            const dayClasses = [
                `${className}__day`,
                isToday ? `${className}__day--today` : '',
                isHighlighted
                    ? `${className}__day--highlighted`
                    : '',
                isHighlighted && isPast
                    ? `${className}__day--completed`
                    : '',
                isHighlighted && isFuture
                    ? `${className}__day--upcoming`
                    : '',
                disabled
                    ? `${className}__day--disabled`
                    : '',
            ]
                .filter(Boolean)
                .join(' ');

            days.push(
                <div
                    key={day}
                    className={dayClasses}
                    title={isHighlighted ? dateStr : undefined}
                >
                    <span className={`${className}__day-number`}>
                        {day}
                    </span>

                    {isHighlighted && (
                        <span
                            className={`${className}__day-indicator`}
                        />
                    )}
                </div>
            );
        }

        return days;
    };

    return (
        <Menu
            open={open}
            onClose={onClose}
            anchorEl={anchorEl}
            className={`${className}__menu`}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            slotProps={{
                paper: {
                    className: `${className}__menu-paper`,
                },
            }}
        >
            <div
                className={`${className} ${
                    disabled ? `${className}--disabled` : ''
                }`}
            >
                {/* Header */}
                <div className={`${className}__header`}>
                    <button
                        type="button"
                        className={`${className}__nav-btn`}
                        onClick={() => moveMonth(-1)}
                        disabled={disabled}
                        aria-label="Previous month"
                    >
                        <ChevronLeft size={19} />
                    </button>

                    <div className={`${className}__month-info`}>
                        <div className={`${className}__month-title`}>
                            {months[currentDate.getMonth()]}
                        </div>

                        <div className={`${className}__year`}>
                            {currentDate.getFullYear()}
                        </div>
                    </div>

                    <button
                        type="button"
                        className={`${className}__nav-btn`}
                        onClick={() => moveMonth(1)}
                        disabled={disabled}
                        aria-label="Next month"
                    >
                        <ChevronRight size={19} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className={`${className}__toolbar`}>
                    <button
                        type="button"
                        className={`${className}__today-btn`}
                        onClick={goToToday}
                        disabled={disabled}
                    >
                        <TodayIcon size={15} />
                        Today
                    </button>
                </div>

                {/* Calendar */}
                <div className={`${className}__calendar-body`}>
                    <div className={`${className}__week-header`}>
                        {weekDays.map((day) => (
                            <div
                                key={day}
                                className={`${className}__week-day`}
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className={`${className}__grid`}>
                        {renderCalendarDays()}
                    </div>
                </div>

                {/* Legend */}
                <div className={`${className}__legend`}>
                    <div className={`${className}__legend-item`}>
                        <span
                            className={`${className}__legend-dot ${className}__legend-dot--completed`}
                        />
                        <span>Completed</span>
                    </div>

                    <div className={`${className}__legend-item`}>
                        <span
                            className={`${className}__legend-dot ${className}__legend-dot--upcoming`}
                        />
                        <span>Scheduled</span>
                    </div>

                    <div className={`${className}__legend-item`}>
                        <span
                            className={`${className}__legend-dot ${className}__legend-dot--today`}
                        />
                        <span>Today</span>
                    </div>
                </div>
            </div>
        </Menu>
    );
}
